import React, { useContext, useEffect, useState } from "react";
import "./index.scss";
import {
  Button,
  Col,
  Divider,
  Row,
  Space,
  Typography,
} from "antd";
import Instructions from "./Instructions/Instructions";
import DraggerArea from "./Dragger/Dragger";
import CVList from "./CVList/CVList";
import FileInfo from "./FileInfo/FileInfo";
import InstructionFooter from "./InstructionFooter/InstructionFooter";
import UploadButton from "./UploadButton/UploadButton";
import { Userprofile } from "../../Interface/Index";
import useMediaQuery from "../../Hooks/useMediaQuery";
import ModalContext from "../../Context/ModalContext/Index";
import ProfileMatcherService from "../../Services/profile.matcher.service";
import { openNotification } from "../Notification/Index";
import LoaderContext from "../../Context/LoaderContext/Index";

interface FileUploaderProps {
  handleNewlyUploadedFile: (file: File) => void;
  handleSelectedProfile: (profile: Userprofile) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  handleNewlyUploadedFile,
  handleSelectedProfile,
}) => {
  const { setLoading } = useContext(LoaderContext);
  const { showUploadCV, setShowUploadCV } = useContext(ModalContext);
  const isNotSmallScreen = useMediaQuery("(max-width: 769px)");
  const [file, setFile] = useState<File | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<Userprofile | null>(
    null
  );
  const [enableContinue, setEnableContinue] = useState<boolean>(false);
  const [savedProfile, setSavedProfile] = useState<Userprofile[] | []>([]);

  const profileMatcherService = new ProfileMatcherService();
  const { isLoading } = useContext(LoaderContext);

  const fetchSavedProfiles = async () => {
    try {
      setLoading(true);
      const data = await profileMatcherService.getAllUploadedProfiles();
      setSavedProfile(
        data.sort(
          (a: Userprofile, b: Userprofile) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      );

      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedProfiles();
  }, []);

  const handleCancelUploadedFile = () => {
    setEnableContinue(false);
    setFile(null);
  };

  const handleCheckedProfile = (profile: Userprofile | null) => {
    setSelectedProfile(profile);
    let enable = profile ? true : false;
    setEnableContinue(enable);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      const validFileTypes = [
        "application/pdf",
        // "application/msword",
        // "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!validFileTypes.includes(file.type)) {
        openNotification(
          "error",
          "",
          // "Please upload a .pdf, .doc, or .docx file."
          "Please upload a .pdf file."
        );
        return;
      }
      setEnableContinue(true);
      setFile(file);
      openNotification("success", "File uploaded", "");
    }
  };

  const handleFile = (file: File) => {
    const validFileTypes = [
      "application/pdf",
      // "application/msword",
      // "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validFileTypes.includes(file.type)) {
      openNotification(
        "error",
        "",
        // "Please upload a .pdf, .doc, or .docx file."
        "Please upload a .pdf file."
      );
      return;
    }

    console.log("🚀 ~ handleFile ~ file:", file);
    setEnableContinue(true);
    setFile(file);
    openNotification("success", "File uploaded", "");
  };

  const handleCancelFile = () => {
    setEnableContinue(false);
    setFile(null);
  };

  const handleClick = () => {
    if (selectedProfile) {
      setShowUploadCV(false);
      handleSelectedProfile(selectedProfile);
    } else if (file) {
      setShowUploadCV(false);
      handleNewlyUploadedFile(file);
    }
  };

  const handleClodeModal = () => {
    setShowUploadCV(false);
  };

  return (
    <Row className="upload_container">
      <Col xs={0} sm={0} md={16} className="dragger_container">
        <Space
          size={16}
          direction="vertical"
          style={{ width: "90%", marginTop: 24 }}
        >
          <DraggerArea
            handleCancelFile={handleCancelFile}
            handleFile={handleFile}
            disabled={selectedProfile ? true : false}
          />
          {savedProfile && savedProfile.length > 0 && (
            <Divider>
              <span>or</span>
            </Divider>
          )}
          {savedProfile && savedProfile.length > 0 && (
            <CVList
              handleChange={handleCheckedProfile}
              savedProfile={savedProfile}
              file={file}
            />
          )}
          <div className="dragger_container_footer">
            <Button
              type="primary"
              onClick={handleClick}
              disabled={!enableContinue}
              loading={isLoading}
            >
              Continue
            </Button>
          </div>
        </Space>
      </Col>
      <Col
        xs={24}
        sm={24}
        md={8}
        className="instructions_container"
        style={{ border: "1px solid #f9f9f9" }}
      >
        <div style={{ padding: "0 16px" }}>
          {savedProfile.length === 0 && isNotSmallScreen && <Instructions />}
          {!isNotSmallScreen && <Instructions />}
          {file === null && savedProfile.length === 0 && (
            <InstructionFooter handleFileChange={handleFileChange} />
          )}

          {file && savedProfile.length === 0 && (
            <FileInfo handleCancel={handleCancelUploadedFile} file={file} />
          )}

          {savedProfile && savedProfile.length > 0 && (
            <div className="mobile_cv_list">
              <CVList
                file={file}
                handleChange={handleCheckedProfile}
                savedProfile={savedProfile}
              />
              {file && (
                <FileInfo file={file} handleCancel={handleCancelUploadedFile} />
              )}

              {!file && (
                <UploadButton
                  handleFileChange={handleFileChange}
                  disabled={!!selectedProfile}
                />
              )}
            </div>
          )}
          {(file || selectedProfile) && (
            <Col className="continue_button_conatiner">
              <Button
                type="primary"
                onClick={handleClick}
                disabled={!enableContinue}
                loading={isLoading}
              >
                Continue
              </Button>
            </Col>
          )}
          <Col>
            <Typography.Paragraph className="info_text">
              {/* Upload PDF, DOC, and DOCX files only. Max 10 MB files are allowed. */}
              Upload PDF formatted files only. Max 10 MB files are allowed.
            </Typography.Paragraph>
          </Col>
        </div>
      </Col>
    </Row>
  );
};

export default FileUploader;
